import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useWizardContext } from './WizardContext';
import type { SpotlightRect } from './types';
import wizardStyles from './wizard.css';

// Export per Remix <Links /> — importa in root.tsx o nella route che usa il wizard
export const wizardLinks = () => [{ rel: 'stylesheet', href: wizardStyles }];

// ---------------------------------------------------------------------------
// Costanti
// ---------------------------------------------------------------------------
const SCROLL_OFFSET = 80; // margine extra durante lo scroll

// ---------------------------------------------------------------------------
// Utility: calcola il rettangolo spotlight
// ---------------------------------------------------------------------------
function getSpotlightRect(selector: string, padding: number): SpotlightRect | null {
  if (typeof window === 'undefined') return null;
  const el = document.querySelector(selector);
  if (!el) return null;
  const rect = el.getBoundingClientRect();
  return {
    top: rect.top - padding,
    left: rect.left - padding,
    width: rect.width + padding * 2,
    height: rect.height + padding * 2
  };
}

// ---------------------------------------------------------------------------
// Componente principale WizardRenderer
// ---------------------------------------------------------------------------

/**
 * Renderizza overlay, spotlight e tooltip wizard.
 * Incluso automaticamente in <WizardProvider>.
 */
export function WizardRenderer() {
  const { activeWizard, currentStep, currentStepIndex, totalSteps, isFirstStep, isLastStep, nextStep, prevStep, closeWizard, goToStep } = useWizardContext();

  const [spotlight, setSpotlight] = useState<SpotlightRect | null>(null);
  const [visible, setVisible] = useState(false);
  const animFrameRef = useRef<number | null>(null);

  const titleId = useId();
  const descId = useId();

  // Calcola spotlight e aggiorna ad ogni resize/scroll
  const updateSpotlight = useCallback(() => {
    if (!currentStep?.target) {
      setSpotlight(null);
      return;
    }
    const padding = currentStep.spotlightPadding ?? 8;
    const rect = getSpotlightRect(currentStep.target, padding);
    setSpotlight(rect);
  }, [currentStep]);

  // Scroll verso l'elemento target
  useEffect(() => {
    if (!currentStep?.target) return;
    const el = document.querySelector(currentStep.target);
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const isInView = rect.top >= SCROLL_OFFSET && rect.bottom <= window.innerHeight - SCROLL_OFFSET;
    if (!isInView) {
      window.scrollBy({
        top: rect.top - SCROLL_OFFSET,
        behavior: 'smooth'
      });
    }
  }, [currentStep]);

  // Update spotlight loop (reactive a scroll/resize)
  useEffect(() => {
    if (!activeWizard) {
      setVisible(false);
      setSpotlight(null);
      return;
    }

    setVisible(false);
    // Piccolo delay per animazione entrata
    const timer = setTimeout(() => {
      updateSpotlight();
      setVisible(true);
    }, 20);

    const handleUpdate = () => {
      animFrameRef.current = requestAnimationFrame(() => {
        updateSpotlight();
      });
    };

    window.addEventListener('scroll', handleUpdate, { passive: true });
    window.addEventListener('resize', handleUpdate, { passive: true });

    return () => {
      clearTimeout(timer);
      window.removeEventListener('scroll', handleUpdate);
      window.removeEventListener('resize', handleUpdate);
      if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
    };
  }, [activeWizard, currentStep, updateSpotlight]);

  // Chiudi con Escape
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeWizard();
      if (e.key === 'ArrowRight' && !isLastStep) nextStep();
      if (e.key === 'ArrowLeft' && !isFirstStep) prevStep();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [closeWizard, nextStep, prevStep, isFirstStep, isLastStep]);

  if (!activeWizard || !currentStep) return null;

  const progressPercent = totalSteps > 1 ? (currentStepIndex + 1) / totalSteps * 100 : 100;
  const nextLabel = isLastStep ? activeWizard.finishLabel ?? 'Fine' : activeWizard.nextLabel ?? 'Avanti →';

  return createPortal(
    <>
			{/* Overlay */}
			{/* eslint-disable-next-line react/forbid-dom-props */}
			<div
        aria-hidden="true"
        className="wizard-overlay"
        style={{
          opacity: visible ? 1 : 0,
          cursor: currentStep.disableOverlayClose ? 'default' : 'pointer',
          backgroundColor: !currentStep.target ? 'rgba(0, 0, 0, 0.68)' : 'transparent'
        }}
        onClick={currentStep.disableOverlayClose ? undefined : closeWizard} />
      

			{/* Spotlight */}
			{spotlight &&
      // eslint-disable-next-line react/forbid-dom-props
      <div aria-hidden="true" className="wizard-spotlight" style={{ top: spotlight.top, left: spotlight.left, width: spotlight.width, height: spotlight.height, opacity: visible ? 1 : 0 }} />
      }

			{/* Tooltip fisso in basso al centro — solo lo spotlight si sposta */}
			{/* eslint-disable-next-line react/forbid-dom-props, jsx-a11y/no-noninteractive-element-interactions, jsx-a11y/click-events-have-key-events */}
			<div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={descId}
        className="wizard-tooltip-wrapper"
        style={{
          opacity: visible ? 1 : 0,
          transform: visible ? 'translateX(-50%) scale(1)' : 'translateX(-50%) scale(0.95)',
          pointerEvents: visible ? 'all' : 'none'
        }}
        onClick={(e) => e.stopPropagation()}>
        
				<div className="card wizard-card" role="document">
					{/* Header */}
					<div className="card-header d-flex align-items-center justify-content-between py-4 px-3 wizard-header">
						<div className="d-flex align-items-center gap-2">
							<span id={titleId} className="wizard-header-title">
								{activeWizard.name}
							</span>
							{currentStep.badge && <span className={`badge bg-${currentStep.badgeColor ?? 'warning'} text-dark wizard-badge`}>{currentStep.badge}</span>}
						</div>
						<button type="button" className="btn-close btn-close-white wizard-close-btn" aria-label="Chiudi guida (o premi Esc)" onClick={closeWizard} />
					</div>

					{/* Progress */}
					{/* eslint-disable-next-line react/forbid-dom-props */}
					<div
            className="wizard-progress-track"
            role="progressbar"
            aria-valuenow={currentStepIndex + 1}
            aria-valuemin={1}
            aria-valuemax={totalSteps}
            aria-label={`Passo ${currentStepIndex + 1} di ${totalSteps}`}>
            
						{/* eslint-disable-next-line react/forbid-dom-props */}
						<div className="wizard-progress-fill" style={{ width: `${progressPercent}%` }} />
					</div>

					{/* Body */}
					<div className="card-body px-4 py-3">
						<p className="text-muted mb-1 wizard-step-counter">
							Passo {currentStepIndex + 1} di {totalSteps}
						</p>
						<h2 id={descId} className="fw-bold mb-2 wizard-step-title h5" style={{ fontSize: '1.25rem' }}>
							{currentStep.title}
						</h2>
						<p className="text-secondary mb-0 wizard-step-description" aria-live="polite" aria-atomic="true">
							{currentStep.description}
						</p>
					</div>

					{/* Footer */}
					<div className="card-footer bg-white border-top d-flex align-items-center justify-content-between py-2 px-3">
						<div className="d-flex gap-1 align-items-center" role="tablist" aria-label="Navigazione step">
							{Array.from({ length: totalSteps }).map((_, i) =>
              // eslint-disable-next-line react/forbid-dom-props
              <button
                key={i}
                type="button"
                role="tab"
                aria-label={`Vai al passo ${i + 1}`}
                aria-selected={i === currentStepIndex ? 'true' : 'false'}
                className={`wizard-dot ${i === currentStepIndex ? 'wizard-dot--active' : 'wizard-dot--inactive'}`}
                onClick={() => goToStep(i)} />

              )}
						</div>
						<div className="d-flex gap-2 align-items-center">
							<button type="button" className="btn btn-link btn-sm text-muted p-0 text-decoration-none wizard-btn-skip" onClick={closeWizard}>
								{activeWizard.skipLabel ?? 'Salta guida'}
							</button>
							{!isFirstStep &&
              <button type="button" className="btn btn-outline-secondary btn-sm wizard-btn-prev" onClick={prevStep}>
									{activeWizard.prevLabel ?? '← Indietro'}
								</button>
              }
							<button type="button" className="btn btn-secondary btn-sm wizard-btn-next" onClick={nextStep}>
								{nextLabel}
							</button>
						</div>
					</div>
				</div>
			</div>
		</>,
    document.body
  );
}